// Derived auth state — accessible modules, roles, and profile read straight from
// the auth slice, which the select-company response populates. The token is never
// decoded; these fields are persisted, so they're correct after both
// company-select and reload.

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { AuthRole } from '@/redux/auth/authSlice';

/**
 * The WEB admin grants (type `admin` only) as module slug → granted verbs, e.g.
 * `{ company_structure: ['view', 'edit'] }`. `null` when access data is absent.
 * The two callers read that null differently on purpose: `selectAdminModuleSlugs`
 * below passes it through to fail OPEN (a hiccup mustn't brick the sidebar),
 * while `useCan` treats it as no grant and DENIES, since an edit affordance the
 * server would reject is worse than a missing one.
 *
 * One selector over the whole slice rather than a per-module selector factory:
 * `createSelector` caches a single result, so a parameterised selector would
 * recompute on every alternating module argument. `useCan` does a plain lookup
 * on this memoised map instead.
 */
export const selectAdminPermissions = createSelector(
  [(state: RootState) => state.auth.permissions],
  (permissions): Record<string, string[]> | null =>
    permissions
      ? Object.fromEntries(
          permissions.filter((p) => p.type === 'admin').map((p) => [p.module, p.permissions]),
        )
      : null,
);

/**
 * Module slugs the user can access in the WEB admin panel (`admin` grants only) —
 * the keys of the map above, so the two can't disagree about which modules are
 * granted or about what `null` means.
 */
export const selectAdminModuleSlugs = createSelector(
  [selectAdminPermissions],
  (byModule): string[] | null => (byModule ? Object.keys(byModule) : null),
);

export const selectAuthRoles = createSelector(
  [(state: RootState) => state.auth.roles],
  (roles): AuthRole[] => roles ?? [],
);

export const selectIsOwner = createSelector([selectAuthRoles], (roles) =>
  roles.some((r) => r.isOwner),
);

export const selectAuthUser = createSelector(
  [(state: RootState) => state.auth.user],
  (user) => user,
);

export const selectCompanyDetail = createSelector(
  [(state: RootState) => state.auth.companyDetail],
  (companyDetail) => companyDetail,
);

/** The current user's own company-user record id — always returned by
 *  select-company, so null only before a company is selected. */
export const selectCompanyUserId = createSelector(
  [(state: RootState) => state.auth.companyUserId],
  (companyUserId) => companyUserId,
);

/** The company owner's company-user record id; null until the post-switch
 *  owner-details fetch lands (or if it failed). */
export const selectOwnerCompanyUserId = createSelector(
  [(state: RootState) => state.auth.ownerCompanyUserId],
  (ownerCompanyUserId) => ownerCompanyUserId,
);
